// 编辑状态相关的类型定义和辅助函数

export interface EditOperation {
  pageId: string
  pageIndex: number
  htmlContent: string
  previousContent: string
  timestamp: number
  operation: 'edit' | 'move' | 'delete' | 'add'
}

export interface GlobalEditHistory {
  stack: EditOperation[]
  currentIndex: number
}

export interface TempEditState {
  htmlContent: string
  lastModified: number
}

export interface TempEditStates {
  [pageId: string]: TempEditState
}

export interface SelectedElement {
  tagName: string
  id?: string
  className?: string
  [key: string]: any
}

export interface EditorState {
  // 编辑模式状态
  isEditMode: boolean

  // 选择状态
  selectedPageIndex: number
  selectedElement: SelectedElement | null

  // 当前可见页面状态
  visiblePageIndex: number
  visiblePageId: string

  // 编辑历史
  globalEditHistory: GlobalEditHistory

  // 临时编辑状态
  tempEditStates: TempEditStates
  hasUnsavedChanges: boolean

  // 页面引用（用于与组件通信）
  pptListRef: any
  pageRefs: { [key: string]: any }
  iframeRefs: { [index: number]: any }
  showUnsaveDialog: boolean
  isConfirmUnsave: boolean
  toUrl: string
}

export const defaultEditorState = (): EditorState => ({
  isEditMode: false,
  selectedPageIndex: -1,
  selectedElement: null,
  visiblePageIndex: 0,
  visiblePageId: '',
  globalEditHistory: {
    stack: [],
    currentIndex: -1,
  },
  tempEditStates: {},
  hasUnsavedChanges: false,
  pptListRef: null,
  pageRefs: {},
  iframeRefs: {},
  showUnsaveDialog: false,
  isConfirmUnsave: false,
  toUrl: '',
})

// 辅助函数：检查是否可以撤销
export function canUndo(history: GlobalEditHistory): boolean {
  return history.currentIndex >= 0
}

// 辅助函数：检查是否可以重做
export function canRedo(history: GlobalEditHistory): boolean {
  return history.currentIndex < history.stack.length - 1
}

// 辅助函数：获取当前实际显示的内容
export function getCurrentContent(pageId: string, tempStates: TempEditStates, originalContent: string): string {
  const tempContent = tempStates[pageId]?.htmlContent
  const result = tempContent || originalContent

  // console.log('getCurrentContent 调试信息:', {
  //   pageId,
  //   hasTempState: !!tempStates[pageId],
  //   tempContentLength: tempContent?.length || 0,
  //   originalContentLength: originalContent?.length || 0,
  //   resultLength: result?.length || 0,
  //   result: result ? `${result.slice(0, 100)}...` : 'null',
  // })

  return result
}

// 辅助函数：发送消息给 iframe
export function sendMessageToIframe(iframe: any, message: any): void {
  if (iframe && iframe.contentWindow) {
    iframe.contentWindow.postMessage(message, '*')
  }
}

// 辅助函数：清理编辑时注入的内容
export function cleanEditingContent(htmlContent: string): string {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return htmlContent
  }

  let cleanedHtml = htmlContent

  // 1. 移除 html 标签上的 data-is-ppt-editor 属性
  cleanedHtml = cleanedHtml.replace(
    /<html([^>]*)\s+data-is-ppt-editor\s*=\s*["']true["']([^>]*)>/gi,
    '<html$1$2>',
  )

  // 2. 使用更精确的方式移除编辑相关元素
  cleanedHtml = removeEditingElements(cleanedHtml)

  // 3. 移除 PPTEditor 相关脚本（更安全的方式）
  cleanedHtml = removePPTEditorScript(cleanedHtml)

  // 4. 移除编辑相关属性
  cleanedHtml = removeEditingAttributes(cleanedHtml)

  // 5. 移除编辑相关样式类
  cleanedHtml = removeEditingClasses(cleanedHtml)

  // 6. 移除 Tailwind CSS 注入的样式
  cleanedHtml = removeTailwindStyles(cleanedHtml)

  // 7. 清理空的属性
  cleanedHtml = cleanEmptyAttributes(cleanedHtml)

  return cleanedHtml
}

// 辅助函数：移除编辑相关元素
function removeEditingElements(html: string): string {
  const editingElementIds = ['highlightBox-tools', 'hoverBox-tools', 'tools-container', 'drag-btn']
  let result = html

  editingElementIds.forEach((id) => {
    // 更精确的匹配：找到特定id的div，然后找到对应的结束标签
    const startPattern = new RegExp(`<div[^>]*id\\s*=\\s*["']${id}["'][^>]*>`, 'gi')
    let match = startPattern.exec(result)

    while (match !== null) {
      const startIndex = match.index
      const startTag = match[0]

      // 从开始标签后查找对应的结束标签
      let depth = 1
      let currentIndex = startIndex + startTag.length
      let endIndex = -1

      while (depth > 0 && currentIndex < result.length) {
        const nextDivStart = result.indexOf('<div', currentIndex)
        const nextDivEnd = result.indexOf('</div>', currentIndex)

        if (nextDivEnd === -1) {
          break
        }

        if (nextDivStart !== -1 && nextDivStart < nextDivEnd) {
          depth++
          currentIndex = nextDivStart + 4
        } else {
          depth--
          if (depth === 0) {
            endIndex = nextDivEnd + 6
            break
          }
          currentIndex = nextDivEnd + 6
        }
      }

      if (endIndex !== -1) {
        result = result.substring(0, startIndex) + result.substring(endIndex)
        // 重置正则表达式的lastIndex
        startPattern.lastIndex = 0
        match = startPattern.exec(result)
      } else {
        break
      }
    }
  })

  return result
}

// 辅助函数：移除PPTEditor脚本
function removePPTEditorScript(html: string): string {
  let result = html

  // 先查找包含 var PPTEditor 的 script 标签
  const scriptPattern = /<script[^>]*>([\s\S]*?)<\/script>/gi
  let match = scriptPattern.exec(result)
  const scriptsToRemove: Array<{ start: number; end: number }> = []

  while (match !== null) {
    const scriptContent = match[1]
    if (scriptContent.includes('var PPTEditor') || scriptContent.includes('PPTEditor=function')) {
      scriptsToRemove.push({
        start: match.index,
        end: match.index + match[0].length,
      })
    }
    match = scriptPattern.exec(result)
  }

  // 从后往前删除，避免索引变化
  scriptsToRemove.reverse().forEach((script) => {
    result = result.substring(0, script.start) + result.substring(script.end)
  })

  return result
}

// 辅助函数：移除编辑相关属性
function removeEditingAttributes(html: string): string {
  const editingAttributes = [
    'data-sk-manual-edit',
    'data-sk-edited',
    'data-sk-deleted',
    'data-sk-reference-id',
    'contenteditable',
  ]

  let result = html
  editingAttributes.forEach((attr) => {
    // 移除属性及其值
    const attrPattern = new RegExp(`\\s+${attr}\\s*=\\s*["'][^"']*["']`, 'gi')
    result = result.replace(attrPattern, '')

    // 移除只有属性名的情况
    const attrOnlyPattern = new RegExp(`\\s+${attr}(?=\\s|>)`, 'gi')
    result = result.replace(attrOnlyPattern, '')
  })

  return result
}

// 辅助函数：移除编辑相关样式类
function removeEditingClasses(html: string): string {
  const editingClasses = [
    'sk-edit-loading',
    'sk-text-wrapper-tag',
    'sk-hide-tag',
  ]

  let result = html
  editingClasses.forEach((className) => {
    // 移除class中的特定类名
    const classPattern = new RegExp(`\\b${className}\\b\\s*`, 'gi')
    result = result.replace(classPattern, '')
  })

  return result
}

// 辅助函数：移除 Tailwind CSS 注入的样式
function removeTailwindStyles(html: string): string {
  let result = html

  // 查找并移除包含 Tailwind 样式的 style 标签
  const stylePattern = /<style[^>]*>([\s\S]*?)<\/style>/gi
  let match = stylePattern.exec(result)
  const stylesToRemove: Array<{ start: number; end: number }> = []

  while (match !== null) {
    const styleContent = match[1]

    // 检查是否为 Tailwind 相关样式
    const isTailwindStyle
      = styleContent.includes('--tw-') // Tailwind CSS 变量

    if (isTailwindStyle) {
      stylesToRemove.push({
        start: match.index,
        end: match.index + match[0].length,
      })
    }

    match = stylePattern.exec(result)
  }

  // 从后往前删除，避免索引变化
  stylesToRemove.reverse().forEach((style) => {
    result = result.substring(0, style.start) + result.substring(style.end)
  })

  // 移除孤立的 Tailwind 相关注释
  result = result.replace(/\/\*[^*]*tailwindcss[^*]*\*\//gi, '')

  // 移除多余的空白行
  result = result.replace(/\n\s*\n\s*\n/g, '\n\n')

  return result
}

// 辅助函数：清理空的属性
function cleanEmptyAttributes(html: string): string {
  let result = html

  // 清理空的class属性
  result = result.replace(/\s+class\s*=\s*["']\s*["']/gi, '')

  // 清理空的style属性
  result = result.replace(/\s+style\s*=\s*["']\s*["']/gi, '')

  return result
}
